import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Container, Row, Tabs, Tab, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  AddNewCard,
  GetAllCard,
  RemoveCard,
  SetCardDefault,
  getSetupIntentData,
} from '../../api/subscription/subscription';
import { BigButton } from '../../common';
import {
  selectGetCardList,
  selectIsCardForm,
  selectIsMakeCheckoutAPICall,
  selectRefetchCard,
  subscriptionActions,
} from '../../redux/slicers/subscriptionSlicer';
import styles from './ListPaymentMethod.module.scss';
import PlanInfoActionCard from './PlanInfoActionCard';
import PaymentCards from './PaymentCards';
import { errorToast, successToast, warningToast } from '../../helpers/toastHelper';
import Loader from '../../components/Loader/Loader';
import { ModalArea } from '../../common/ModalButtonArea';
import { emailIsValid, wait } from '../../helpers/jsHelper';
import { useUserContext } from '../../contexts/UserContext';
import { icons } from '../../assets';
import { CHECKOUT_ADD_PAYMENT_METHOD_POPUP_FORM, MAKE_CHECKOUT_API_CALL, PAYMENT_METHOD_TYPE } from './subscriptionHelper';
import ActionLink from './ActionLink';
import PlusIcon from '../../components/icons/PlusIcon';
import { LogConsole } from '../../helpers/logEvents';
import { callTrackEvent } from '../../helpers/segmentHelper';
import { userPilotEvents } from '../../common/Userpilot/userpilotHelper';
const { ExclamationIcon, BankAccountIconV2, CardIconV2 } = icons;

const initialBankValues = {
  name: '',
  email: '',
};

export default function ListPaymentMethod(props) {
  const { currentUser, orgOwnerDetail } = useUserContext();
  const dispatch = useDispatch();
  const { results } = useSelector(selectGetCardList);
  const refetchCard = useSelector(selectRefetchCard);
  const addCard = useSelector(selectIsCardForm);
  const checkoutAPICallStatus = useSelector(selectIsMakeCheckoutAPICall);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const [paymentMethodType, setPaymentMethodType] = useState(PAYMENT_METHOD_TYPE.CARD);
  const [bankDetails, setBankDetails] = useState(initialBankValues);
  const [bankErrors, setBankErrors] = useState(initialBankValues);
  const {trackClickEvent} = userPilotEvents();

  const useOptions = () => {
    const options = useMemo(() => ({
      hidePostalCode: true,
    }));
    return options;
  };
  const options = useOptions();

  useEffect(() => {
    if (!addCard) return;
    setBankDetails({
      name: currentUser?.organization_detail?.name || orgOwnerDetail?.name,
      email: currentUser?.organization_detail?.billing_email || orgOwnerDetail?.email,
    });
  }, [addCard]);

  useEffect(() => {
    if (refetchCard) {
      getAllCards();
    }
    return () => closeModal();
  }, [refetchCard]);

  const closeModal = (makeCheckoutAPICall) => {
    setLoading(false);
    setIsSubmitting(false);
    setBankErrors(initialBankValues);
    setPaymentMethodType(PAYMENT_METHOD_TYPE.CARD);
    dispatch(subscriptionActions.setShowCardForm(false));
    if(checkoutAPICallStatus === CHECKOUT_ADD_PAYMENT_METHOD_POPUP_FORM && makeCheckoutAPICall){
      dispatch(subscriptionActions.setMakeCheckoutAPICall(MAKE_CHECKOUT_API_CALL));
    }
  };

  const handleAddNewCard = async () => {
    setIsSubmitting(true);
    const res = await getSetupIntentData();
    const cardElement = elements.getElement(CardElement);
    if (!res && !res?.IntentData) {
      errorToast('Unable to get intent data. Please try again later.');
      closeModal();
      return;
    }

    const clientSecret = res?.IntentData?.client_secret?.toString();
    if (!clientSecret) {
      errorToast('Invalid client secret key. Please try again later.');
      closeModal();
      return;
    }

    await stripe
      .confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: currentUser?.organization_detail?.name,
          },
        },
      })
      .then(async function (result) {
        if (result.error) {
          errorToast(result.error.message || 'Something went wrong. Please try again!');
          // closeModal();
          setIsSubmitting(false);
        } else {
          if (result.setupIntent.status === 'succeeded') {
            savePaymentMethod(result.setupIntent.payment_method);
          }
        }
      });
  };

  const savePaymentMethod = async (id = null) => {
    if (!id) {
      errorToast('Require stripe payment method Id.');
      return;
    }
    const formData = new FormData();
    formData.append('stripe_payment_method_id', id);
    await dispatch(AddNewCard(formData));
    addSegmentDataSavePayment('CARD')
    closeModal();
  };

  const addSegmentDataSavePayment = (method) => {
    if (props?.dataForSegment?.Current_Plan_Name) {
      const copyData = JSON.parse(JSON.stringify(props?.dataForSegment));
      copyData['Payment_Method'] = method;
      callTrackEvent("Account Details Saved", copyData, true)
    }
  }

  const handleBankPaymentMethodAdd = async () => {
    if (!bankDetails.name || !bankDetails.email) return;

    setIsSubmitting(true);
    const res = await getSetupIntentData();

    if (!res && !res?.IntentData) {
      errorToast('Unable to get intent data. Please try again later.');
      closeModal();
      return;
    }

    LogConsole('handleBankPaymentMethodAdd res', res?.IntentData);
    const clientSecret = res?.IntentData?.client_secret?.toString();

    let email = bankDetails.email || currentUser?.user?.email;
    const splitEmail = email.split('@');
    email = `${splitEmail[0]}+test_email@${splitEmail[1]}`;
    // email = `jatin+test_email@imagine.io`;

    LogConsole('handleBankPaymentMethodAdd email', email);

    stripe
      .collectBankAccountForSetup({
        clientSecret: clientSecret,
        params: {
          payment_method_type: PAYMENT_METHOD_TYPE.US_BANK_ACCOUNT,
          payment_method_data: {
            billing_details: {
              name: bankDetails.name || currentUser?.user?.name,
              email: email,
            },
          },
        },
        expand: ['payment_method'],
      })
      .then((res) => {
        const { setupIntent, error } = res;
        LogConsole('handleBankPaymentMethodAdd collectBankAccountForSetup', res);

        if (error) {
          console.error(error.message);
          // PaymentMethod collection failed for some reason.

          errorToast('Unable to get bank details. Please try again later.');
          closeModal();
        } else if (setupIntent.status === 'requires_payment_method') {
          // Customer canceled the hosted verification modal. Present them with other
          // payment method type options.

          // errorToast("Customer canceled the hosted verification modal.");
          closeModal();
        } else if (setupIntent.status === 'requires_confirmation') {
          // We collected an account - possibly instantly verified, but possibly
          // manually-entered. Display payment method details and mandate text
          // to the customer and confirm the intent once they accept
          // the mandate.

          stripe.confirmUsBankAccountSetup(clientSecret).then(async (res) => {
            const { setupIntent, error } = res;
            LogConsole('handleBankPaymentMethodAdd confirmUsBankAccountSetup', res);

            if (error) {
              console.error(error.message);
              // The payment failed for some reason.

              errorToast('Unable to confirm bank account. Please try again later.');
              closeModal();
            } else if (setupIntent.status === 'requires_payment_method') {
              // Confirmation failed. Attempt again with a different payment method.

              errorToast(
                'Unable to confirm payment method. Please try with another payment method or try again later.'
              );
              closeModal();
            } else if (setupIntent.status === 'succeeded') {
              // Confirmation succeeded! The account is now saved.
              // Display a message to customer.

              // we are not calling api to submit the payment method id as backend is adding the payment method from the webhook
              // savePaymentMethod(setupIntent.payment_method);
              await wait(3000);
              await dispatch(GetAllCard());
              successToast('Payment method added');
              addSegmentDataSavePayment('US BANK ACCOUNT');
              closeModal(true);
            } else if (setupIntent.next_action?.type === 'verify_with_microdeposits') {
              // The account needs to be verified via microdeposits.
              // Display a message to consumer with next steps (consumer waits for
              // microdeposits, then enters a statement descriptor code on a page sent to them via email).
              warningToast('Please check your email to verify your bank account.');
              closeModal();
            }
          });
        }
      });
  };

  const getAllCards = async () => {
    !loading && setLoading(true);
    const res = await dispatch(GetAllCard());
    if (res || !res) {
      addCard && dispatch(subscriptionActions.setShowCardForm(false));
      if(checkoutAPICallStatus === CHECKOUT_ADD_PAYMENT_METHOD_POPUP_FORM){
        dispatch(subscriptionActions.setMakeCheckoutAPICall(MAKE_CHECKOUT_API_CALL));
      }
      setLoading(false);
    }
  };

  const handleMakeDefault = async (value) => {
    if (value?.id && !value?.is_default) {
      const payload = {
        is_default: true,
      };
      setLoading(true);
      const res = await dispatch(SetCardDefault(payload, value?.id));
      if (!res) {
        setLoading(false);
      }
    }
  };

  const handleRemoveCard = async (value) => {
    if (value?.id) {
      setLoading(true);
      const res = await dispatch(RemoveCard(value?.id));
      if (!res) {
        setLoading(false);
      }
    }
  };

  const paymentMethodActionMenu = [
    { id: 1, title: 'Make Default', icon: null, onClick: handleMakeDefault },
    { id: 2, title: 'Remove', icon: null, onClick: handleRemoveCard },
  ];

  const handleFilterTopDefaultCard = () => {
    let defaultCard = [],
      normalCard = [];
    if (results && results?.length > 0) {
      defaultCard = results?.filter((a) => a?.is_default === true) || [];
      normalCard = results?.filter((a) => a?.is_default !== true) || [];
      // LogConsole('DEBUG___PaymentCards', [...defaultCard, ...normalCard]);
      return [...defaultCard, ...normalCard];
    } else {
      return [];
    }
  };

  const handleTabChange = (type) => {
    if (isSubmitting) {
      warningToast('Your form is being submitted. Please do not refresh the page or switch tabs.');
      return;
    }
    setPaymentMethodType(type);
  };

  const addPaymentMethod = () => {
    if (props?.dataForSegment?.Current_Plan_Name) {
      const copyData = JSON.parse(JSON.stringify(props?.dataForSegment));
      callTrackEvent("Add Payment Details click", copyData, true)
    }
    dispatch(subscriptionActions.setShowCardForm(!addCard))
  }

  return (
    <>
      <PlanInfoActionCard
        className={props?.className}
        containerStyle={props?.containerStyle}
        fullWidth={props?.fullWidth}
        cardTitleStyle={props?.cardTitleStyle}
        {...(props?.planInfoProps || {})}
        title="Payment method"
        {...(!props?.designV2
          ? {
              actionText: 'Add Payment Method',
              onClick: () => addPaymentMethod(),
            }
          : {})}>
        {loading && <Loader position="absolute" loading backdrop />}
        {results && results?.length > 0 ? (
          <>
            <ul className={styles.paymentList}>
              {results &&
                results?.length > 0 &&
                handleFilterTopDefaultCard()?.map((list) => {
                  const brand =
                    list?.payment_type?.toLowerCase() === PAYMENT_METHOD_TYPE.US_BANK_ACCOUNT
                      ? 'Bank account'
                      : list?.card_brand;
                  const endingDigit = list?.last4 || list?.account_number;

                  let actionMenu = paymentMethodActionMenu;
                  if (list?.is_default) {
                    actionMenu = paymentMethodActionMenu?.filter((a) => a.id !== 1);
                  }

                  return (
                    <PaymentCards
                      {...list}
                      key={list?.id}
                      brand={brand}
                      endingDigit={endingDigit}
                      actionMenu={actionMenu}
                      item={list}
                      isPrimary={list?.is_default}
                      designV2={props?.designV2}
                    />
                  );
                })}
            </ul>
          </>
        ) : (
          <>
            {/* {loading ? (
              <Loader position="absolute" loading={loading} backdrop />
            ) : ( */}
            No payment methods are available.
            {/* )} */}
          </>
        )}
        {props?.designV2 ? (
          <ActionLink
            title="Add Payment Method"
            onClick={() => {
              trackClickEvent("Add payment details", {
                plan: "Creator",
                is_yearly: props?.planInfoProps?.isYearly
              })
              addPaymentMethod()
            }}
            titleClassName="text-decoration-none mx-2"
            icon={<PlusIcon width={22} />}
          />
        ) : null}
      </PlanInfoActionCard>

      <ModalArea
        showModal={addCard}
        setShowModal={closeModal}
        title={'Add a payment method'}
        animation={true}
        centered={true}
        size="md"
        contentClassName={styles.paymentMethodModel}
        backdrop={isSubmitting ? 'static' : true}
        body={
          <Container className="w-100 pt-3 px-0">
            <Tabs
              className={`mb-3 pt-0 ${styles.tabs}`}
              mountOnEnter
              unmountOnExit
              activeKey={paymentMethodType}
              onSelect={handleTabChange}>
              <Tab
                eventKey={PAYMENT_METHOD_TYPE.CARD}
                title={
                  <span>
                    <CardIconV2 width={16} />
                    &nbsp;Card
                  </span>
                }>
                <Row className="w-100 px-0 m-0">
                  <Col xs={12} className="mb-2">
                    <CardElement
                      stripe={stripe}
                      options={{
                        ...options,
                        placeholder: 'Card Number',
                      }}
                      className={styles.formField}
                    />
                  </Col>
                  <Col md={12}>
                    <BigButton
                      disabled={isSubmitting}
                      className="w-100 bigButton lf-btn-1 btn-v2"
                      isLoading={isSubmitting}
                      buttonTitle={'Save Payment Method'}
                      onClick={() => handleAddNewCard()}
                    />
                  </Col>
                </Row>
              </Tab>

              <Tab
                eventKey={PAYMENT_METHOD_TYPE.US_BANK_ACCOUNT}
                title={
                  <span>
                    <BankAccountIconV2 width={16} />
                    &nbsp;US bank account
                  </span>
                }>
                <Row className="w-100 px-0 m-0">
                  <Col md={12} className="mb-3">
                    <Form.Group className="form-outline">
                      {bankErrors.name ? (
                        <Form.Label className="text-danger pwd-overflow">
                          <ExclamationIcon />
                          <span>{bankErrors.name}</span>
                        </Form.Label>
                      ) : (
                        <Form.Label className="title-color float-left">
                          <span>Billing name</span>
                        </Form.Label>
                      )}
                      <Form.Control
                        className={`w-100 ${bankErrors.name ? 'input-error' : ''} ${
                          styles.formField
                        }`}
                        type={'text'}
                        name="name"
                        onChange={(e) =>
                          setBankDetails({
                            ...bankDetails,
                            name: e.target.value,
                          })
                        }
                        maxLength={50}
                        value={bankDetails.name}
                        onBlur={(e) =>
                          setBankErrors({
                            ...bankErrors,
                            name: e.target.value ? '' : 'Name is required.',
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12} className="mb-3">
                    <Form.Group className="form-outline">
                      {bankErrors.email ? (
                        <Form.Label className="text-danger pwd-overflow">
                          <ExclamationIcon />
                          <span>{bankErrors.email}</span>
                        </Form.Label>
                      ) : (
                        <Form.Label className="title-color float-left">
                          <span>Billing email</span>
                        </Form.Label>
                      )}
                      <Form.Control
                        className={`w-100 ${bankErrors.email ? 'input-error' : ''} ${
                          styles.formField
                        }`}
                        type={'text'}
                        name="email"
                        onChange={(e) =>
                          setBankDetails({
                            ...bankDetails,
                            email: e.target.value,
                          })
                        }
                        maxLength={50}
                        value={bankDetails.email}
                        onBlur={(e) => {
                          setBankErrors({
                            ...bankErrors,
                            email: e.target.value ? '' : 'Email is required.',
                          });
                          if (bankDetails.email) {
                            setBankErrors({
                              ...bankErrors,
                              email: emailIsValid(bankDetails.email) ? '' : 'Invalid email!!!',
                            });
                          }
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <BigButton
                      disabled={isSubmitting}
                      className="w-100 bigButton lf-btn-1 btn-v2"
                      isLoading={isSubmitting}
                      buttonTitle={'Proceed'}
                      onClick={() => handleBankPaymentMethodAdd()}
                    />
                  </Col>
                </Row>
              </Tab>
            </Tabs>
          </Container>
        }
      />
    </>
  );
}
